import { Controller } from "stimulus"
import { surfaceFieldError, removeFieldErrorState } from "validators/validation_display"

// this hash is used by pages w/embedded subforms (incomes/deductions/benefits) to map driver questions ("Yes"/"No" + subform)
// each key is a driver question (the "Yes" answer on the radio button)'s  `id` (without the `#`)
// and the value is an object with `list` and `entry` selectors.
const DRIVER_ENTRY_LISTS = {
  has_job_income: { list: '#job_income .incomes-list', entry: '.income' },
  has_self_employment_income: { list: '#self_employed_incomes .self-employed-incomes-list', entry: '.income' },
  has_unemployment_income: { list: '#unemployment_income .unemployment-incomes-list', entry: '.unemployment-income' },
  has_other_income: { list: '.other-incomes-list', entry: '.other-income' },
  has_american_indian_alaskan_native_income: { list: '#ai_an_income .ai-an-incomes-list', entry: '.ai-an-income' },
  has_deductions: { list: '.deductions-list', entry: '.deduction' },
  has_enrolled_health_coverage: { list: '.benefits-list.is_enrolled', entry: '.benefit' },
  has_eligible_health_coverage: { list: '.benefits-list.is_eligible', entry: '.benefit' }
}

// edit trigger selectors across the entry types (income/other income/deduction/benefit)
// we use these to scroll and focus on a field in a saved entry that is incomplete
const ENTRY_EDIT_TRIGGERS = 'a.income-edit, a.other-income-edit, a.unemployment-income-edit, a.ai-an-income-edit, a.deduction-edit, a.edit-benefit'

// accessible-validation behavior for the FAA Income & Coverage pages (tax_info, incomes, deductions, benefits, other_questions)
// mounted on each page's form/wrapper
//
// how it works:
//  - keep conditionally-revealed required fields `required` attr in sync with their visibility
//     * most of the the show/hide is driven by the legacy step_conditionals.js (some via
//       an ajax callback) so finalizeForm/checkValidity enforces them only while shown
//     * this controller observes each container's `class` rather than racing the legacy change handlers
//     * without this observer, a static `required` on a hidden control makes checkValidity() fail on a visibility:hidden field
//  - custom per-field validations
//     * includes rules native `required`/checkValidity can't express (phone digit count,
//       start/end date order, pregnancy due date, MaineCare denial 90-day window, primary caregiver selection, money > $0)
//     * defined once in #applyFieldValidations and run by BOTH entrypoints, so the rule set is identical and only the surfacing differs
//     * validate() — on real <form> pages/subforms, ahead of form-validation#submit
//     * #gateNavigation() — on the form-less driver-question pages, where "Continue" is a nav link
//
// selectors absent on a given page resolve to nothing, so the same controller can be mounted across the section's pages.

// this controller is separate from the form_validation controller as the logic involved is much more complex (mostly due to the subforms)
// and it is easier to reason about in isolation
// NOTE: for future updates, we'd also _ideally_ aim to include any changes to the "progress-nav-container" for the income and coverage section in this controller
// SIDENOTE: the same can be said for the logic currently housed in components/financial_assistance/app/assets/javascripts/financial_assistance/step_conditionals.js, but this is a bit beyond the scope of this controller and would require a more significant refactor to move it here
export default class extends Controller {
  connect() {
    // exit early if accessible form validation is not enabled
    if (document.documentElement.dataset.accessibleFormValidation !== 'true') return

    // container: the element whose `.hide` class reflects visibility
    // fields: the controls whose `required` should mirror that visibility
    // these containers specifically are the ones that are conditionally required based on other fields
    this.conditionalRequiredRules = [
      { container: '.is_joint_tax_filing', fields: ['#is_joint_tax_filing_yes', '#is_joint_tax_filing_no'] },
      { container: '.claimed_as_tax_dependent_by', fields: ['#applicant_claimed_as_tax_dependent_by'] },
      // "why no SSN" reason (dropdown case): shown only when "applied for an SSN" = No, required while shown
      { container: '.no-ssn-reason-group', fields: ['#no_ssn_reason'] }
    ]

    this.#syncConditionalRequired()

    // mutation observer to keep the conditional-required rules in sync with visibility changes
    this.observer = new MutationObserver(() => this.#syncConditionalRequired())

    // observes each container for class changes to detect visibility toggles
    this.conditionalRequiredRules.forEach(({ container }) => {
      const el = this.element.querySelector(container)
      if (el) this.observer.observe(el, { attributes: true, attributeFilter: ['class'] })
    })

    // clear the caretaker group error as the user edits (surfacing happens on submit via validate()),
    // mirroring the clear-on-edit behavior form-validation applies to single fields
    this._onCaregiverEdit = () => this.#clearPrimaryCaregiverIfSatisfied()
    this.#primaryCaregiverInputs().forEach(el => el.addEventListener('change', this._onCaregiverEdit))

    // money amount fields (income/deduction amount, ESI employee cost): only allow digits and the
    // currency-formatting characters $ , . to be typed/pasted (this also blocks "-", letters, etc.)
    this._restrictMoneyInput = (event) => {
      if (event.data && /[^0-9$,.]/.test(event.data)) event.preventDefault()
    }
    this.#moneyFields().forEach(field => field.addEventListener('beforeinput', this._restrictMoneyInput))

    // employer phone: only allow digits and phone-format characters ( ) - space
    // NOTE: the legacy jQuery mask isn't applied on income edit forms, so enforce it here for every subform
    this._restrictPhoneInput = (event) => {
      if (event.data && /[^0-9()\- +]/.test(event.data)) event.preventDefault()
    }
    this.#phoneFields().forEach(field => field.addEventListener('beforeinput', this._restrictPhoneInput))

    // for the income and coverage pages w/subforms (incomes/deductions/benefits): the "Continue" button is a nav link, not a form
    // submit, so it has to be gated here —> block navigation when a visible driver question is unanswered.
    this._continueButton = this.element.querySelector('#btn-continue')
    if (this._continueButton) {
      this._onContinueClick = (event) => this.#gateNavigation(event)
      this._continueButton.addEventListener('click', this._onContinueClick, true) // capture: run before the anchor navigates / legacy bubble handlers
      this._onDriverChange = (event) => {
        const radio = event.target
        if (!radio.matches('.driver-question input[type="radio"]')) return
        // clears customValidity + the group outline + the group's primary ("select an option") feedback
        removeFieldErrorState(radio)
        // also hide the sibling "add at least one" feedback — removeFieldErrorState only resolves the group's primary feedback
        // without this, answering "Yes" (add-entry error) then switching to "No" would leave the "or select No" error message
        radio.closest('fieldset')?.querySelectorAll('.invalid-feedback.d-block')
          .forEach(feedback => feedback.classList.remove('d-block'))
      }
      this.element.addEventListener('change', this._onDriverChange)

      // some tricky logic here -> saved entries (incomes, benefits, etc.) are injected via AJAX (remote:true subforms)
      // not a radio change, so a "add at least one" error wouldn't clear when the user actually adds an entry

      // we remove the error by observing the target entry-list containers directly for childList changes and then show/hide error messages
      const listSelectors = [...new Set(Object.values(DRIVER_ENTRY_LISTS).map(config => config.list))].join(', ')
      const lists = this.element.querySelectorAll(listSelectors)
      if (lists.length) {
        this.entryListObserver = new MutationObserver(() => this.#clearSatisfiedEntryErrors())
        lists.forEach(list => this.entryListObserver.observe(list, { childList: true, subtree: true }))
      }
    }
  }

  disconnect() {
    this.observer?.disconnect()
    if (this._onCaregiverEdit) {
      this.#primaryCaregiverInputs().forEach(el => el.removeEventListener('change', this._onCaregiverEdit))
    }
    if (this._restrictMoneyInput) {
      this.#moneyFields().forEach(field => field.removeEventListener('beforeinput', this._restrictMoneyInput))
    }
    if (this._restrictPhoneInput) {
      this.#phoneFields().forEach(field => field.removeEventListener('beforeinput', this._restrictPhoneInput))
    }
    if (this._continueButton && this._onContinueClick) {
      this._continueButton.removeEventListener('click', this._onContinueClick, true)
    }
    this.entryListObserver?.disconnect()
    if (this._onDriverChange) {
      this.element.removeEventListener('change', this._onDriverChange)
    }
  }

  // submit-time entrypoint for real <form> pages/subforms (wired via `submit->…#validate`, ahead of
  // form-validation#submit): stamps customValidity so the shared finalizeForm surfaces the field's
  // `.invalid-feedback` and scroll/focuses it. form-validation's clear-on-edit listener resets single-field customValidity.
  validate() {
    this.#applyFieldValidations()
  }

  // the single set of custom per-field rules (things native `required`/checkValidity can't express)
  // called by BOTH entrypoints — validate() (form submit) and #gateNavigation (nav-link click) — so each
  // rule is defined once and enforced everywhere; only the surfacing differs (finalizeForm vs the nav gate)
  // every rule self-guards (missing field / hidden / empty), so running the whole set on any page is safe:
  // the rules whose fields aren't on the current page simply no-op
  #applyFieldValidations() {
    this.#validatePregnancyDueDate()
    this.#validateMedicaidDenialDate()
    this.#validateMoneyAmounts()
    this.#validateDateRange()
    this.#validatePhoneNumbers()
    this.#validatePrimaryCaregiver()
  }

  // employer phone must have at least 10 digits
  #phoneFields() {
    return this.element.querySelectorAll('input.phone_number')
  }

  #validatePhoneNumbers() {
    this.#phoneFields().forEach(field => {
      // hidden or empty (native `required` owns emptiness) -> no digit-count error
      if (this.#isHidden(field) || field.value.trim() === '') {
        field.setCustomValidity('')
        return
      }
      const digits = field.value.replace(/\D/g, '')
      field.setCustomValidity(digits.length >= 10 ? '' : 'invalid')
    })
  }

  // validates if both start and end are filled, and end < start
  // if either is empty, no error (native `required` owns emptiness)
  #validateDateRange() {
    const endField = this.#visibleField('input[name$="[end_on]"]')
    if (!endField) return
    const startField = this.#visibleField('input[name$="[start_on]"]')
    if (!startField || !startField.value || !endField.value) {
      endField.setCustomValidity('')
      return
    }
    const start = new Date(`${startField.value}T00:00:00`)
    const end = new Date(`${endField.value}T00:00:00`)
    endField.setCustomValidity(end < start ? 'invalid' : '')
  }

  #visibleField(selector) {
    return Array.from(this.element.querySelectorAll(selector)).find(field => !this.#isHidden(field))
  }

  // visibility test that matches this app's hide conventions. `.hide`/`.hidden` extend Bootstrap's
  // `.sr-only` (position:absolute) plus `visibility:hidden` (!!!!NOT `display:none`!!!!) so a hidden element
  // keeps a non-null offsetParent; `offsetParent === null` alone only catches `display:none`. `visibility`
  // is inherited, so a field inside a `.hide` container reports `visibility:hidden` via getComputedStyle.
  // Without this, hidden conditional fields (e.g. the benefits date fields inside `.hide` blocks) are
  // treated as visible and can silently block navigation.
  #isHidden(el) {
    if (!el) return true
    if (el.offsetParent === null) return true
    return window.getComputedStyle(el).visibility === 'hidden'
  }

  // incomes/other_incomes/deductions/benefits pages navigation gate
  // blocks "Continue" while a visible driver question is unanswered

  #gateNavigation(event) {
    // the below covers 2 conditions that would otherwise double-handle the mid-edit case:
    // 1. the legacy unsaved-changes modal disables the nav buttons while a subform is open, so this handler never runs
    // 2. the benefit editors reveal an .edit-benefit-form, which is a hidden form that holds the real required inputs (not disabled), so checkValidity works even while hidden; this handler runs
    if (this.#currentlyEditing()) return

    this.#clearNavErrors()
    // run the same custom field rules validate() uses, so page-level fields (e.g. the MaineCare 90-day
    // window) get their customValidity stamped before #navProblems reads checkValidity(). Runs after
    // clearNavErrors (which only resets visual state, not customValidity).
    this.#applyFieldValidations()
    const problems = this.#navProblems()
    if (!problems.length) return // valid -> allow navigation

    event.preventDefault()
    event.stopImmediatePropagation()

    // surface EVERY invalid driver question's error state, not just the first. The deeper cases —
    // a saved entry missing a field, or a page-level required field — open an edit form / grab focus,
    // so only the first of those is surfaced. Scroll + focus land on the topmost problem overall.
    let deepShown = false
    problems.forEach((problem, index) => {
      if (problem.kind === 'driver') {
        this.#surfaceNavProblem(problem, index === 0)
      } else if (!deepShown) {
        deepShown = true
        this.#surfaceNavProblem(problem, index === 0)
      }
    })
  }

  #currentlyEditing() {
    return !!this.element.querySelector('#nav-buttons a.disabled, .edit-benefit-form:not(.hidden)')
  }

  // check driver questions in DOM order
  // for each, check 
  // (a) answered
  // (b) if "Yes" ensure >=1 entry
  // (c) check if each saved entry field is complete
  // returns ALL problems (topmost first) so every invalid driver question can be surfaced, not just the first
  // only adds one error per driver question
  #navProblems() {
    const problems = []
    const groups = this.element.querySelectorAll('.driver-question .accessible-form-radio-group')
    for (const group of groups) {
      if (this.#isHidden(group)) continue // hidden driver question (e.g. conditional benefit blocks)
      const radios = group.querySelectorAll('input[type="radio"]')
      if (!radios.length) continue
      const name = radios[0].name
      const answered = this.element.querySelector(`input[type="radio"][name="${name}"]:checked`)
      if (!answered) { problems.push({ kind: 'driver', group, reason: 'unanswered' }); continue }

      if (answered.value !== 'true') continue
      const listConfig = DRIVER_ENTRY_LISTS[name]
      if (!listConfig) continue

      const entries = this.#entriesFor(listConfig)
      if (entries.length === 0) { problems.push({ kind: 'driver', group, reason: 'add_entry' }); continue }

      for (const entry of entries) {
        const field = this.#firstInvalidEntryField(entry)
        if (field) { problems.push({ kind: 'entry', entry, field }); break }
      }
    }

    // page-level required fields that are not inside any subform 
    // (e.g. the benefits conditional date fields) — enforce only while visible, so a hidden conditional block never blocks navigation.
    const field = this.#firstInvalidPageField()
    if (field) problems.push({ kind: 'field', field })

    return problems
  }

  #entriesFor({ list, entry }) {
    const entries = []
    this.element.querySelectorAll(list).forEach(container => {
      entries.push(...container.querySelectorAll(entry))
    })
    return entries
  }

  // this method targets fields for validation that are 
  // 1. not in a subform
  // 2. not in a page-level form (tax_info, other_questions)
  // 3. don't fall into the driver-question + subform pattern (incomes, deductions, benefits)
  // they are targeted via the faa-nav-required class, which is applied to the conditional date fields in the benefits subforms
  // enforced only while visible, so a hidden conditional block never blocks navigation
  #firstInvalidPageField() {
    return Array.from(this.element.querySelectorAll('.faa-nav-required'))
      .find(field => !this.#isHidden(field) && !field.checkValidity()) || null
  }

  // the hidden edit form holds the entry's (income/deduction/benefit) real required inputs (not disabled)
  // checkValidity works even while hidden; returns the first empty/invalid required input, or null if complete
  #firstInvalidEntryField(entry) {
    const form = entry.querySelector('form')
    if (!form) return null
    return this.#relevantRequiredFields(entry, form).find(el => !el.disabled && !el.checkValidity()) || null
  }

  // required inputs to check for a saved entry
  // for an esi benefit, the hidden edit form still holds ALL of the mutually-exclusive HRA / non-HRA / MVSQ groups
  // (handleEsiFields only prunes them once the form is revealed), so checking every [required] while hidden would false-positive on the inapplicable group
  // the `.benefit-show` data-esi/data-hra/data-mvsq attributes tell us which group applies, so we drop the inapplicable groups' fields up front (mirroring handleEsiFields)
  #relevantRequiredFields(entry, form) {
    const required = Array.from(form.querySelectorAll('[required]'))
    const show = entry.querySelector('.benefit-show')
    if (!show || show.dataset.esi !== 'true') return required // non-benefit / non-ESI: no exclusive groups, so all required fields are relevant

    const isHra = show.dataset.hra === 'true'
    const isMvsq = show.dataset.mvsq === 'true'
    return required.filter(field => {
      if (isHra && field.closest('.non-hra-questions')) return false
      if (!isHra && field.closest('.hra-questions')) return false
      if (!isMvsq && field.closest('.mvsq-questions')) return false
      return true
    })
  }

  // `focus` scrolls the problem into view and moves focus to it -> pass false when surfacing
  // secondary problems so only the topmost one grabs focus.
  #surfaceNavProblem(problem, focus = true) {
    if (problem.kind === 'driver') {
      problem.group.classList.add('is-invalid-group')
      const feedback = problem.reason === 'add_entry'
        ? problem.group.parentElement.querySelector('.add-entry-feedback')
        : problem.group.nextElementSibling
      if (feedback?.classList.contains('invalid-feedback')) feedback.classList.add('d-block')
      if (focus) {
        problem.group.scrollIntoView({ behavior: 'smooth', block: 'center' })
        problem.group.querySelector('input[type="radio"]')?.focus()
      }
      return
    }
    if (problem.kind === 'field') {
      // a visible page-level required field (e.g. a benefits conditional date field)
      surfaceFieldError(problem.field)
      if (focus) {
        problem.field.scrollIntoView({ behavior: 'smooth', block: 'center' })
        problem.field.focus()
      }
      return
    }
    // saved entry is field-incomplete: open its Edit form, then surface + focus the first missing field
    problem.entry.querySelector(ENTRY_EDIT_TRIGGERS)?.click()
    problem.field.closest('form')?.classList.add('was-validated')
    surfaceFieldError(problem.field)
    if (focus) {
      problem.field.scrollIntoView({ behavior: 'smooth', block: 'center' })
      problem.field.focus()
    }
  }

  // removes the "unanswered" and "add at least one" error state from all driver questions
  #clearNavErrors() {
    this.element.querySelectorAll('.driver-question .accessible-form-radio-group.is-invalid-group')
      .forEach(group => group.classList.remove('is-invalid-group'))
    this.element.querySelectorAll('.driver-question .invalid-feedback.d-block')
      .forEach(feedback => feedback.classList.remove('d-block'))
    // page-level required fields (e.g. the benefits date fields) surface their feedback via d-block too,
    // and they aren't inside a .driver-question — clear them here so a fixed field's error doesn't linger
    // on the next Continue click. (customValidity is left alone; it's recomputed before #navProblems.)
    this.element.querySelectorAll('.faa-nav-required')
      .forEach(field => field.nextElementSibling?.classList?.remove('d-block'))
  }

  // clear the "add at least one" error for any driver whose list now has >=1 entry (entry was added via AJAX)
  // called from the entry MutationObserver, so error state tracks entry presence live
  #clearSatisfiedEntryErrors() {
    this.element.querySelectorAll('.driver-question .accessible-form-radio-group.is-invalid-group').forEach(group => {
      const name = group.querySelector('input[type="radio"]')?.name
      const config = DRIVER_ENTRY_LISTS[name]
      if (!config || this.#entriesFor(config).length === 0) return
      group.classList.remove('is-invalid-group')
      group.classList.add('px-0')
      group.closest('fieldset')?.querySelectorAll('.invalid-feedback.d-block')
        .forEach(feedback => feedback.classList.remove('d-block'))
    })
  }

  // money amounts: must be greater than $0.00 //
  #moneyFields() {
    return this.element.querySelectorAll('input[name$="[amount]"], input[name$="[employee_cost]"]')
  }

  #validateMoneyAmounts() {
    this.#moneyFields().forEach(field => {
      // hidden or empty (if empty is handled by native `required`) -> no money error
      if (this.#isHidden(field) || field.value.trim() === '') {
        field.setCustomValidity('')
        return
      }
      const amount = parseFloat(field.value.replace(/[^0-9.-]/g, ''))
      field.setCustomValidity(Number.isFinite(amount) && amount > 0 ? '' : 'invalid')
    })
  }

  ///////////////////////////////////////////////////////////////////////

  // only runs if the field is visible and filled (date must be in the future)
  // empty value is left to the native `required` handling).
  #validatePregnancyDueDate() {
    const field = this.element.querySelector('#applicant_pregnancy_due_on')
    if (!field) return
    if (this.#isHidden(field) || !field.value) {
      field.setCustomValidity('')
      return
    }
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const entered = new Date(`${field.value}T00:00:00`)
    field.setCustomValidity(entered > today ? '' : 'invalid')
  }

  // denial date must fall within the last 90 days: on or before today, and no more than 90 days ago
  // only the visible, filled field is checked; emptiness is owned by native `required`
  // This is a page-level (form-less) field, so it's enforced through the nav gate (#firstInvalidPageField) rather than a form submit
  #validateMedicaidDenialDate() {
    const field = this.element.querySelector('#medicaid_cubcare_due_on')
    if (!field) return
    if (this.#isHidden(field) || !field.value) {
      field.setCustomValidity('')
      return
    }
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const ninetyDaysAgo = new Date(today)
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90)
    const entered = new Date(`${field.value}T00:00:00`)
    const withinWindow = entered >= ninetyDaysAgo && entered <= today
    field.setCustomValidity(withinWindow ? '' : 'invalid')
  }

  ///////////////////////////////////////////////////////////////////////

  ///////////////////// primary caregiver handling /////////////////////
  // requirement: if "Yes" selected, needs >= 1 caretaker selected
  // + remove error if "No" selected or at least one caretaker is checked
  #primaryCaregiverInputs() {
    return this.element.querySelectorAll(
      '#is_primary_caregiver_yes, #is_primary_caregiver_no, .primary-caregiver-for-group input[type="checkbox"]'
    )
  }

  #validatePrimaryCaregiver() {
    const group = this.element.querySelector('.primary-caregiver-for-group')
    const caregiverYes = this.element.querySelector('#is_primary_caregiver_yes')
    if (!group || !caregiverYes) return
    const checkboxes = group.querySelectorAll('input[type="checkbox"]')
    if (!checkboxes.length) return

    const requireSelection = caregiverYes.checked && !this.#isHidden(group)
    const anyChecked = Array.from(checkboxes).some(cb => cb.checked)

    if (requireSelection && !anyChecked) {
      // one member of the group carries the customValidity so the form is blocked and focusable
      checkboxes[0].setCustomValidity('invalid')
      group.classList.add('is-invalid-group')
      group.querySelector('.invalid-feedback')?.classList.add('d-block')
    } else {
      this.#clearPrimaryCaregiverError(group, checkboxes)
    }
  }

  #clearPrimaryCaregiverIfSatisfied() {
    const group = this.element.querySelector('.primary-caregiver-for-group')
    const caregiverYes = this.element.querySelector('#is_primary_caregiver_yes')
    if (!group || !caregiverYes) return
    const checkboxes = group.querySelectorAll('input[type="checkbox"]')
    if (!checkboxes.length) return

    const anyChecked = Array.from(checkboxes).some(cb => cb.checked)
    if (!caregiverYes.checked || anyChecked) this.#clearPrimaryCaregiverError(group, checkboxes)
  }

  #clearPrimaryCaregiverError(group, checkboxes) {
    checkboxes.forEach(cb => cb.setCustomValidity(''))
    group.classList.remove('is-invalid-group')
    group.querySelector('.invalid-feedback')?.classList.remove('d-block')
  }

  ///////////////////////////////////////////////////////////////////////

  // the most important part of this controller (along w/gateNavigation)
  // keep the conditional-required fields in sync with their visibility
  // without this check, a hidden required field (e.g. a benefits conditional date field) would block navigation

  #syncConditionalRequired() {
    this.conditionalRequiredRules.forEach(({ container, fields }) => {
      const el = this.element.querySelector(container)
      if (!el) return
      const required = !el.classList.contains('hide')
      fields.forEach(selector => {
        const field = this.element.querySelector(selector)
        if (field) field.required = required
      })
    })
  }
}

